import useDeviceDetect from 'Hooks/useDeviceDetect';
import { store } from 'Models/Store';
import React from 'react';
import { feeType } from 'Models/Enums';
import { formatPriceDisplay } from 'Util/_HumanWritten/PriceFormattingUtils';
import { observer } from 'mobx-react';
import { TransactionPricingResult } from 'Services/Api/_HumanWritten/PricingService/PricingResult';
import { setWizardData } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardDataStore';
import { useBookingWizardPricingCacheStore } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/BookingWizardPricingCacheStore';
import { AddAnotherBookingButton } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/WizardSteps/Cart/AddAnotherBookingButton';
import { AddServiceFeeButton } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/WizardSteps/Cart/AddServiceFeeButton';
import { CartCalculationLineItem } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/WizardSteps/Cart/CartLineItems/CartCalculationLineItem';
import { TripBookingSummarySideBarTotal } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/WizardSteps/Cart/CartLineItems/TripBookingSummarySideBarTotal';
import FerryPromoGiftCard from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/WizardSteps/Cart/FerryPromoGiftCard';
import { tripSummaryLocationType } from 'Views/Components/_HumanWritten/BookingWizard/FerryTripBookingWizard/WizardSteps/Cart/TripBookingSummaryCard';
import If from 'Views/Components/If/If';
import AddedFeeLines from './CartLineItems/AddedFeeLines';
import CreditCardFeeLine from './CartLineItems/CreditCardFeeLine';
import GiftUsageLines from './CartLineItems/GiftUsageLines';
import { isNotNullOrUndefined } from 'Util/TypeGuards';
import { getFerryBookingTransactionIdFromStorage } from 'Services/Api/_HumanWritten/BookingService/BookingService';
import { BookingWizardData } from '../../BookingWizardData/BaseBookingWizardData';
import DiscountLines from './CartLineItems/DiscountLines';
import { appConfigStore } from 'Models/AppConfigStore';
import { whiteLabelStore } from 'Models/WhiteLabelStore';
import { summaryTotalFigures } from './summaryTotalFigures';

export interface TripBookingSummaryTotalProps {
	transactionPricingResults?: TransactionPricingResult;
	afterPayment: boolean;
	cartView: boolean;
	onClearGiftCertificate?: (id: string) => void;
	onRemoveDiscount?: (discountId: string) => void;
	onClearFee?: (feeId: string) => void;
	onRemoveManagerDiscount?: () => void;
	onRemoveFee?: (feeType: feeType, departureDiscountAmount?: number, returnDiscountAmount?: number) => void;
	tripSummaryLocation: tripSummaryLocationType;
	wizardData?: BookingWizardData;
	refetch?: boolean;
	setRefetch?: (refetch: boolean) => void;
	bookingIds?: string[];
}

/**
 * @deprecated Superseded by `ClassBookingSummary/ClassBookingSummaryTotal`, which sites with fare types use in
 * the cart, the sidebar, check-in and on the booking success page. Still used everywhere on a site without
 * fare types (temporarily) and for freight. Kept until those move across; do not build on it.
 */
function TripBookingSummaryTotal({
	transactionPricingResults,
	afterPayment,
	cartView,
	onClearGiftCertificate,
	onRemoveDiscount,
	onRemoveManagerDiscount,
	onRemoveFee,
	tripSummaryLocation,
	wizardData,
	onClearFee,
	refetch,
	setRefetch,
	bookingIds,
}: TripBookingSummaryTotalProps) {
	const { isMobileBookingWizard } = useDeviceDetect();
	const isCartView = cartView || afterPayment;
	const transactionId = getFerryBookingTransactionIdFromStorage() ?? undefined;
	const { gstLabel } = appConfigStore;

	const { getTotalBookingPrice } = useBookingWizardPricingCacheStore();

	const {
		totalAlterationFee,
		totalTrailerFee,
		totalCheckInForfeitFee,
		totalRemovalFee,
		checkInForfeitFeeLabel,
		removalFeeLabel,
		departurePriceIncrease,
		returnPriceIncrease,
		basePriceIncrease,
		gst,
		parkingPriceForSubtotal,
		feesListedBelowSubtotal,
	} = summaryTotalFigures(transactionPricingResults, wizardData, store.isStaff);
	const { gstPercentage } = whiteLabelStore.config;

	// Server's totalPrice already includes any server parking, so nothing extra is added to the TOTAL;
	// the subtotal breakdown still surfaces the server parking amount as its own contribution.
	const parkingPriceForTotal = 0;

	return (
		<div className="total-booking-price__container">
			<If condition={!isCartView}>
				<TripBookingSummarySideBarTotal
					lineItemDisplayName="TOTAL"
					lineItemPrice={formatPriceDisplay(
						(transactionPricingResults?.total ?? 0) +
							getTotalBookingPrice(bookingIds, wizardData?.bookingToEdit) +
							parkingPriceForTotal,
						transactionPricingResults?.isAlteration ?? false,
					)}
				/>
			</If>
			<If condition={isCartView && isNotNullOrUndefined(transactionPricingResults)}>
				<div className="booking-total-price__card">
					<div className="booking-total-price__card-inner">
						<div className="booking-total-price__breakdown">
							<CartCalculationLineItem
								keyToUse="subtotal-line-item-key"
								lineItemDisplayName="Subtotal"
								lineItemPrice={formatPriceDisplay(
									(transactionPricingResults?.subtotal ?? 0) +
										parkingPriceForSubtotal -
										feesListedBelowSubtotal,
									transactionPricingResults?.isAlteration ?? false,
								)}
								className="subtotal-line-item"
							/>
							{/* Check-in supplies its own onClearFee (CheckInReviewContent -> useCheckInReview),
							    so it is passed straight through like everywhere else. It used to be dropped
							    here, which took the cross off the row and made the fee unremovable in
							    check-in - the one flow where a staff member is most likely to need to. The
							    row is still only removable when the fee option says so and the viewer is
							    staff; AddedFeeLines applies both. */}
							<AddedFeeLines
								fees={
									transactionPricingResults?.lines?.filter(x => x.key === 'ServiceFee') ?? []
								}
								onRemoveFee={onClearFee}
							/>
							<If condition={store.isStaff && totalAlterationFee > 0}>
								<CartCalculationLineItem
									lineItemDisplayName="Alteration fee"
									lineItemPrice={formatPriceDisplay(totalAlterationFee, false)}
									hasRemovalButton={store.can('WaiveFees') && tripSummaryLocation !== 'booking-success'}
									removalOnClick={() => {
										if (store.can('WaiveFees') && onRemoveFee) {
											onRemoveFee('ALTERATION');
										}
									}}
								/>
							</If>
							<If condition={totalTrailerFee > 0}>
								<CartCalculationLineItem
									lineItemDisplayName="Trailer removal fee:"
									lineItemPrice={formatPriceDisplay(totalTrailerFee, false)}
									hasRemovalButton={store.can('WaiveFees') && tripSummaryLocation !== 'booking-success'}
									removalOnClick={() => {
										if (store.can('WaiveFees') && onRemoveFee) {
											onRemoveFee('CANCELLATION');
										}
									}}
								/>
							</If>
							{/*
							  * The general removal fee, shown to customers as well as staff: it is a charge
							  * they are paying, not a staff-only adjustment, which is why it is not behind
							  * the `isStaff` gate the alteration fee sits behind. No removal button — unlike
							  * the trailer and forfeit fees there is no server-side clear for this fee type
							  * yet, and offering a cross that did nothing would be worse than not offering
							  * one.
							  */}
							<If condition={totalRemovalFee > 0}>
								<CartCalculationLineItem
									lineItemDisplayName={removalFeeLabel || 'Removal fee'}
									lineItemPrice={formatPriceDisplay(totalRemovalFee, false)}
								/>
							</If>
							{/*
							  * Manager+ only, and worth understanding before using: this fee is not a charge
							  * levied on top, it is the value the reduction gave up. Removing it does not
							  * refund the customer — it drops the retention, so the fare reduction stands
							  * with nothing holding the difference and the transaction owes money back.
							  * That is a deliberate manager override ("actually, give it back"), not a
							  * tidy-up. It can be put back from `Add service fee`, which re-measures it.
							  */}
							<If condition={totalCheckInForfeitFee > 0}>
								<CartCalculationLineItem
									lineItemDisplayName={checkInForfeitFeeLabel || 'Check-in forfeit fee'}
									lineItemPrice={formatPriceDisplay(totalCheckInForfeitFee, false)}
									hasRemovalButton={store.can('WaiveFees') && tripSummaryLocation !== 'booking-success'}
									removalOnClick={() => {
										if (store.can('WaiveFees') && onRemoveFee) {
											onRemoveFee('CHECK_IN_FORFEIT');
										}
									}}
								/>
							</If>
							<If
								condition={
									store.can('ApplyManagerDiscount') &&
									isNotNullOrUndefined(
										transactionPricingResults?.lines?.find(
											x => x.key === 'ManagerDiscount',
										),
									)
								}
							>
								<CartCalculationLineItem
									lineItemDisplayName="Manager discount:"
									lineItemPrice={formatPriceDisplay(
										transactionPricingResults?.lines?.find(
											x => x.key === 'ManagerDiscount',
										)?.price ?? 0,
										transactionPricingResults?.isAlteration ?? false,
									)}
									hasRemovalButton={store.can('ApplyManagerDiscount') && tripSummaryLocation !== 'booking-success'}
									removalOnClick={() => {
										if (store.can('ApplyManagerDiscount') && onRemoveManagerDiscount) {
											onRemoveManagerDiscount();
										}
									}}
								/>
							</If>
							<If
								condition={
									// Adds a manager discount, so it asks the capability the
									// add-manager-discount endpoint carries rather than the tier.
									store.can('ApplyManagerDiscount') &&
									basePriceIncrease > 0 &&
									!transactionPricingResults?.lines?.some(x => x.key === 'ManagerDiscount')
								}
							>
								<CartCalculationLineItem
									lineItemDisplayName="Ticket price increase"
									lineItemPrice={formatPriceDisplay(basePriceIncrease, false)}
									hasRemovalButton={store.can('ApplyManagerDiscount') && tripSummaryLocation !== 'booking-success'}
									removalOnClick={() => {
										// 'OTHER' is not a fee removal: CartSummary turns it into an add-manager-discount.
										if (store.can('ApplyManagerDiscount') && onRemoveFee) {
											onRemoveFee('OTHER', departurePriceIncrease, returnPriceIncrease);
										}
									}}
								/>
							</If>
							<DiscountLines
								discounts={
									transactionPricingResults?.lines?.filter(x => x.key === 'Discount') ?? []
								}
								onClearDiscount={onRemoveDiscount}
							/>
							<GiftUsageLines
								giftCertificateUsages={
									transactionPricingResults?.lines?.filter(
										x => x.key === 'GiftCertificate',
									) ?? []
								}
								onClearGiftCertificate={onClearGiftCertificate}
							/>
							<CreditCardFeeLine
								lineItem={
									transactionPricingResults?.lines?.find(x => x.key === 'CreditCard') ?? null
								}
								gstLabel={gstLabel}
							/>
							<CartCalculationLineItem
								keyToUse="total-line-item-key"
								lineItemDisplayName="TOTAL"
								lineItemDescription={
									gstPercentage > 0
										? `${transactionPricingResults?.lines?.find(x => x.key === 'Gst')?.description ?? 'GST'} included`
										: undefined
								}
								lineItemPrice={formatPriceDisplay(
									(transactionPricingResults?.total ?? 0) + parkingPriceForTotal,
									transactionPricingResults?.isAlteration ?? false,
								)}
								lineItemSubPrice={
									gst > 0 && gstPercentage > 0 ? formatPriceDisplay(gst, false, true) : undefined
								}
								className="total-cart-line"
							/>
						</div>
						<If
							condition={
								store.can('ManageServiceFees') &&
								(tripSummaryLocation === 'cart' || tripSummaryLocation === 'check-in')
							}
						>
							<AddServiceFeeButton
								transactionPricingResults={transactionPricingResults}
								tripSummaryLocation={tripSummaryLocation}
								wizardMode={wizardData?.wizardMode ?? 'CREATE'}
								transactionId={transactionId}
								refetch={refetch}
								setRefetch={setRefetch}
							/>
						</If>
						<If condition={tripSummaryLocation === 'cart'}>
							<AddAnotherBookingButton
								wizardData={wizardData}
								onUpdateWizardData={setWizardData}
								redirectURL={
									isMobileBookingWizard ? '/booking-wizard/search' : '/booking-wizard/tickets'
								}
								// No "Add service fee" button above it (customer view) — add top spacing.
								className={store.isStaff ? undefined : 'add-another-booking-button__standalone'}
							/>
						</If>
					</div>
					<If condition={!afterPayment}>
						<FerryPromoGiftCard
							transactionId={transactionId ?? ''}
							onAddGiftCertificate={() => {
								if (setRefetch) {
									setRefetch(!refetch);
								}
							}}
							forCheckIn={tripSummaryLocation === 'check-in'}
						/>
					</If>
				</div>
			</If>
		</div>
	);
}

export default observer(TripBookingSummaryTotal);
